/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-use-before-define */
import CaseMessageBody from '@brands/ChatWindow/ChatComponents/CaseMessageBody';
import {
  useConversationBySid,
  useConversationParticipants,
  useTwilioConversationBySid,
  useWindowSize,
} from '@brands/hooks';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import useSubscribe, { Subscriber } from '@brands/hooks/useSubscribe';
import { CasePayload } from '@brands/services/cases/createNewCase';
import { editCaseById } from '@brands/services/cases/editCase';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { getConversation } from '@brands/services/communication/getConversation';
import { IGMConversation } from '@brands/services/communication/types/IGMConversation';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { useParams } from 'react-router-dom';

import CaseConversationList from '../../ChatWindow/ChatComponents/CaseConversationList';
import Container from '../../Components/Container/Container';
import { Loading } from '../../Components/LoadingSpinner/Loading';
import { useAppSelector } from '../../hooks/useReduxHook';
import styles from './styles.module.scss';

const CaseChatWindow = (): JSX.Element => {
  const screenSize = useWindowSize();
  const [thisCase, setCase] = useState<ICase>();
  const backPath = sessionStorage.getItem('lastNonMessagesPath') || '/';
  const routeCaseId = useParams().caseId as string;
  const routeConversationSid = useParams().conversationSid || null;
  const [conversationSid, setConversationSid] = useState<string | null>(routeConversationSid);
  const twilioConversation = useTwilioConversationBySid(conversationSid);
  const [readOnlyConversation, setReadOnlyConversation] = useState<IGMConversation | null>(null);
  const [conversations, setConversations] = useState<
    { conversationType: string; conversation?: IGMConversation; createNewConversation?: boolean }[]
  >([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const isMobile = screenSize.width < 970;
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userInfo } = useAppSelector(selectAuth);
  const [render, setRender] = useState(true);
  const roleNavigate = useRoleNavigate();
  const { conversation, refreshConversation } = useConversationBySid(conversationSid || null);
  const { participants, participantJoin } = useConversationParticipants(twilioConversation);

  useEffect(() => {
    refreshConversation();
  }, [participants]);

  const previousParticipantJoin = useRef(participantJoin);

  useEffect(() => {
    if (previousParticipantJoin.current !== participantJoin) {
      window.location.reload();
    }
    previousParticipantJoin.current = participantJoin;
  }, [participantJoin]);

  const updateThisCase = async (thisCaseId: number): Promise<void> => {
    const payload: CasePayload = {
      status: 'in_progress_provider',
    };

    await editCaseById(thisCaseId, payload, userInfo.role.name);
  };

  const getReadOnlyConversation = (): Promise<IGMConversation | null> => {
    if (conversationSid) return getConversation(conversationSid);
    return Promise.resolve(null);
  };

  const updateUnreadNotification = (): void => {
    if (
      thisCase?.patient_care_advisor_conversation_stats &&
      thisCase?.patient_care_advisor_conversation_stats?.length > 0 &&
      thisCase?.patient_care_advisor_conversation_stats[0]?.conversation_sid === conversationSid
    ) {
      setCase({
        ...thisCase,
        patient_care_advisor_conversation_stats: null,
      });
    }
    if (
      thisCase?.patient_provider_conversation_stats &&
      thisCase?.patient_provider_conversation_stats?.length > 0 &&
      thisCase?.patient_provider_conversation_stats[0]?.conversation_sid === conversationSid
    ) {
      setCase({
        ...thisCase,
        patient_provider_conversation_stats: null,
      });
    }
    if (
      thisCase?.provider_care_advisor_conversation_stats &&
      thisCase?.provider_care_advisor_conversation_stats.length > 0 &&
      thisCase?.provider_care_advisor_conversation_stats[0]?.conversation_sid === conversationSid
    ) {
      setCase({
        ...thisCase,
        provider_care_advisor_conversation_stats: null,
      });
    }
  };

  const getCurrentCase = async (caseId: string): Promise<void> => {
    const currentCase = await getOneCase(
      Number(caseId),
      {
        include_patient_care_advisor_conversation:
          userInfo.role.name !== UserRoleName.Admin &&
          userInfo.role.name !== UserRoleName.SuperAdmin &&
          userInfo.role.name !== UserRoleName.OrganizationAdmin,
        include_patient_provider_conversation:
          userInfo.role.name !== UserRoleName.Admin &&
          userInfo.role.name !== UserRoleName.SuperAdmin &&
          userInfo.role.name !== UserRoleName.OrganizationAdmin,
        include_provider_care_advisor_conversation:
          userInfo.role.name !== UserRoleName.Patient &&
          userInfo.role.name !== UserRoleName.Admin &&
          userInfo.role.name !== UserRoleName.SuperAdmin &&
          userInfo.role.name !== UserRoleName.OrganizationAdmin,
      },
      userInfo.role.name,
    );
    setCase(currentCase);
    if (
      userInfo.role.name === UserRoleName.Provider &&
      currentCase.provider_id === userInfo.id &&
      currentCase.status === 'accepted_provider'
    ) {
      updateThisCase(currentCase.id);
    }
  };

  const changeCaseUnreadIndicator = async (): Promise<void> => {
    if (thisCase?.id) {
      try {
        const updatedCase = await getOneCase(
          thisCase?.id,
          {
            include_patient_care_advisor_conversation: true,
            include_patient_provider_conversation: true,
            include_provider_care_advisor_conversation: userInfo.role.name !== UserRoleName.Patient,
          },
          userInfo.role.name,
        );
        if (updatedCase) {
          setCase({
            ...thisCase,
            patient_care_advisor_conversation_sid: updatedCase.patient_care_advisor_conversation_sid,
            patient_provider_conversation_sid: updatedCase.patient_provider_conversation_sid,
            provider_care_advisor_conversation_sid: updatedCase.provider_care_advisor_conversation_sid,
            patient_care_advisor_conversation_stats: updatedCase.patient_care_advisor_conversation_stats,
            patient_provider_conversation_stats: updatedCase.patient_provider_conversation_stats,
            provider_care_advisor_conversation_stats: updatedCase.provider_care_advisor_conversation_stats,
          });
        }
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    }
  };

  const onMessage = useCallback(
    async (message: Record<string, any>) => {
      if (thisCase && Number(message.case_id) === Number(thisCase.id)) {
        if (['case_new_message_created', 'case_new_conversation_created'].includes(message.command)) {
          // Refresh the case's pointer sids so a superseded open thread is
          // recognized as archived even when the sidebar (which normally
          // handles this event) is unmounted, e.g. on mobile.
          changeCaseUnreadIndicator();
        }
      }
    },
    [thisCase],
  );

  const subscriber: Subscriber = useMemo(
    () => ({
      onMessage,
      commands: ['case_new_message_created', 'case_new_conversation_created'],
    }),
    [onMessage],
  );

  const { subscribe, unsubscribe } = useSubscribe();

  useEffect(() => {
    const subscriptionId = subscribe(subscriber);

    return () => unsubscribe(subscriptionId);
  }, [subscribe, unsubscribe, subscriber]);

  useEffect(() => {
    getCurrentCase(routeCaseId);
  }, []);

  useEffect(() => {
    if (!routeConversationSid) {
      return;
    }
    setConversationSid(routeConversationSid);
  }, [routeConversationSid]);

  useEffect(() => {
    getReadOnlyConversation().then((readOnly) => {
      setReadOnlyConversation(readOnly);
    });
  }, [conversationSid]);

  if (render) {
    return (
      <Container
        patientDashboard
        childClassName={styles.dashboard}
        containerStyle={{
          justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
          overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
        }}
      >
        <div className={styles.CaseChatContainer}>
          <div className={styles.CaseChatContainerDiv}>
            {(!isMobile || (isMobile && !conversationSid)) && (
              <div className={styles.CaseChatContainerLeftDiv}>
                <Button
                  type="button"
                  variant="text"
                  className={styles.backButton}
                  sx={{
                    width: 'fit-content',
                  }}
                  onClick={() => roleNavigate(backPath)}
                >
                  <MdKeyboardArrowLeft />
                  Back
                </Button>
                <div>
                  <span className={styles.CaseChatContainerLeftDivSpan}>Messages</span>
                  <CaseConversationList
                    thisCase={thisCase}
                    activeConversationSid={conversationSid}
                    setActiveConversationSid={setConversationSid}
                    setConversations={setConversations}
                    conversations={conversations}
                    setCase={setCase}
                    setLoading={setLoading}
                    changeCaseUnreadIndicator={changeCaseUnreadIndicator}
                  />
                </div>
              </div>
            )}
            {downloadLoading ? (
              <Loading dotted />
            ) : (!isMobile || (isMobile && conversationSid)) && conversationSid ? (
              <div className={styles.CaseChatContainerRightDiv}>
                <div className={styles.CaseChatContainerRightDivContent}>
                  {(!isMobile || (isMobile && conversationSid)) && conversationSid ? (
                    <CaseMessageBody
                      twilioConversation={twilioConversation}
                      readOnlyConversation={readOnlyConversation}
                      isMobile={isMobile}
                      conversationSid={conversationSid}
                      setConversationSid={setConversationSid}
                      conversation={conversation}
                      isProfileCard={false}
                      setLeaveModalOpen={setLeaveModalOpen}
                      leaveModalOpen={leaveModalOpen}
                      currentCase={thisCase}
                      updateUnreadNotification={updateUnreadNotification}
                      conversationType={
                        conversations.find(
                          (selectedConversation) =>
                            selectedConversation.conversation &&
                            selectedConversation.conversation.sid === conversationSid,
                        )?.conversationType
                      }
                      isArchivedConversation={
                        // A thread is archived/read-only when the case no
                        // longer points at it. Derived from the case's
                        // current pointer sids — kept fresh by this window's
                        // own conversation-event subscriber — rather than the
                        // sidebar list, which isn't mounted on mobile / deep
                        // links and would fail open.
                        !!conversationSid &&
                        (!thisCase ||
                          ![
                            thisCase.patient_care_advisor_conversation_sid,
                            thisCase.patient_provider_conversation_sid,
                            thisCase.provider_care_advisor_conversation_sid,
                          ].includes(conversationSid))
                      }
                      patientCurrentState={thisCase?.patient_current_state}
                      loading={loading}
                      setLoading={setDownloadLoading}
                    />
                  ) : !loading ? (
                    <div className={styles.chatBody}>
                      <span className={`fs-unmaskx ${styles.centerText}`}>
                        Choose a user on the left to enter the conversation
                      </span>
                    </div>
                  ) : (
                    <Loading dotted />
                  )}
                </div>
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
      </Container>
    );
  }

  return <Loading fullScreen />;
};

export default CaseChatWindow;
